import Button from "../core/Button";
import Strings from "../core/data/Strings";
import ResizableContainer from "../core/ResizableContainer";
import ResizableScene from "../core/ResizableScene";
import ScaleManager from "../core/ScaleManager";
import Medal from "../game/Medal";
import { SnowParticles } from "../game/SnowParticles";
import GameColors from "../GameColors";
import { SaveData } from "../SaveData";
import Fonts from "../utils/Fonts";

export type StoryBeat = {
	text: string;
	image: string;
};

export default class CertificatePopup extends ResizableContainer {
	scene: ResizableScene;

	private _onResize: () => void;

	private _onComplete: () => void;
	private _onCompleteContext: CallbackContext;

	private certificateBacker: Phaser.GameObjects.Image;
	private certificate: Phaser.GameObjects.Container;
	private _currentPage = 0;

	private image: Phaser.GameObjects.Image;
	private storyTextGroup: Phaser.GameObjects.Container;

	private headerText: Phaser.GameObjects.Text;
	private downloadButton: Button;
	private nextButton: Button;

	constructor(scene: ResizableScene, saveData: SaveData, onComplete?: () => void, onCompleteContext?: CallbackContext) {
		super(scene, 0, 0);
		scene.soundManager.play("gameCompleteSFX");
		this._onComplete = onComplete;
		this._onCompleteContext = onCompleteContext;

		const overlay = scene.add.image(0, 0, "popupOverlay");
		overlay.setInteractive();
		this.add(overlay);

		this.setSize(scene.viewport.width, scene.viewport.height);
		SnowParticles.create(scene, this);

		const popupContainer = scene.add.container();
		this.add(popupContainer);

		const backer = this.scene.add.image(0, 0, "storyBacker");
		popupContainer.add(backer);

		const image = (this.image = scene.add.image(0, 0, "storyImg3"));
		popupContainer.add(image);

		const textGroup = (this.storyTextGroup = this.scene.add.container());
		popupContainer.add(textGroup);

		const bubble = scene.add.image(0, 0, "storyTextBacker");
		textGroup.add(bubble);
		textGroup.setSize(bubble.width, bubble.height);

		const storyText = this.scene.add.text(0, 0, Strings.get("SearchAndRescueAchieved"), {
			color: " #686ab5",
			fontSize: "30px",
			fontFamily: Fonts.MontserratBold,
			align: "center",
			wordWrap: { width: bubble.width - 150 },
		});
		storyText.setLineSpacing(20);
		textGroup.add(storyText);

		const headerBg = scene.add.image(0, 0, "popupHeader");
		const headerText = (this.headerText = scene.add.text(0, 0, Strings.get("Congratulations"), {
			color: GameColors.WHITE,
			fontSize: "70px",
			fontFamily: Fonts.MontserratBold,
			align: "center",
			wordWrap: { width: bubble.width - 150 },
		}));

		const leftDeco = scene.add.image(0, 0, "leftDeco");
		const rightDeco = scene.add.image(0, 0, "rightDeco");
		popupContainer.add([headerBg, headerText, leftDeco, rightDeco]);

		const downloadBtn = (this.downloadButton = scene.add.button(0, 0, "downloadBtn", 0, this.download, this, true));
		popupContainer.add(downloadBtn);

		const nextButton = (this.nextButton = scene.add.button(0, 0, "nextBtn", 0, this.nextPage, this, true));
		popupContainer.add(nextButton);

		const certificateBacker = (this.certificateBacker = scene.add.image(0, 0, "certificateBacker").setVisible(false));
		popupContainer.add(certificateBacker);

		this.certificate = this.scene.add.container().setScale(0.5);
		const certificateImage = this.scene.add.image(0, 0, "certificateBg");
		this.certificate.setSize(certificateImage.width * 0.5, certificateImage.height * 0.5);

		const certificateHeaderBg = scene.add.image(0, 0, "certificateHeader");
		const certificateHeaderText = scene.add.text(0, 0, Strings.get("Congratulations"), {
			color: GameColors.WHITE,
			fontSize: "70px",
			fontFamily: Fonts.MontserratBold,
			align: "center",
		});

		const certificateLogo = scene.add.image(0, 0, "loadingLogo").setScale(1);
		const leftStars = scene.add.image(0, 0, "certificateStars");
		const rightStars = scene.add.image(0, 0, "certificateStars").setFlipX(true);

		const certificateBodyTextBg = scene.add.image(0, 0, "certificateTextBacker");
		const certificateBodyText = scene.add.text(0, 0, Strings.get("CertificatePresented2"), {
			color: GameColors.WHITE,
			fontSize: "35px",
			fontFamily: Fonts.MontserratBold,
			align: "center",
			wordWrap: { width: certificateBodyTextBg.width - 200 },
		});
		certificateBodyText.setLineSpacing(20);

		let medalAverage = 0;
		for (let i = 0; i < saveData.medalsEarned.length; i++) {
			const medalEarned = saveData.medalsEarned[i];
			if (medalEarned == Medal.BRONZE) medalAverage += 1;
			else if (medalEarned == Medal.SILVER) medalAverage += 2;
			else if (medalEarned == Medal.GOLD) medalAverage += 3;
		}

		medalAverage = medalAverage / saveData.medalsEarned.length;
		console.log("Medal average: " + medalAverage);

		let medalTex = "popupMedal";
		if (medalAverage == 3) medalTex += Medal.GOLD;
		else if (medalAverage < 3 && medalAverage >= 2) medalTex += Medal.SILVER;
		else medalTex += Medal.BRONZE;

		const medal = this.scene.add.image(0, 0, medalTex).setScale(1.1);

		this.certificate.add([
			certificateImage,
			certificateHeaderBg,
			certificateHeaderText,
			certificateLogo,
			leftStars,
			rightStars,
			certificateBodyTextBg,
			certificateBodyText,
			medal,
		]);

		// hide certificate assets
		downloadBtn.setVisible(false);
		this.certificate.setVisible(false);

		this._onResize = (): void => {
			ScaleManager.ScaleBackgroundFromZone(overlay, scene.viewport);
			scene.alignIn.Center(overlay, scene.viewport);
			scene.alignIn.Center(popupContainer, scene.viewport);
			scene.alignIn.Center(image, backer, 0, -310);

			// certificate
			scene.alignIn.Center(certificateBacker, backer, 0, -75);
			scene.alignIn.Center(this.certificate, scene.viewport, 0, -75);
			scene.alignIn.TopCenter(certificateHeaderBg, certificateImage);
			scene.alignIn.Center(certificateHeaderText, certificateHeaderBg);
			scene.alignIn.Center(certificateLogo, certificateImage, 0, -220);
			scene.alignTo.LeftCenter(leftStars, certificateLogo, -40, 90);
			scene.alignTo.RightCenter(rightStars, certificateLogo, -40, 90);
			scene.alignIn.Center(certificateBodyTextBg, certificateImage, 0, 70);
			scene.alignIn.Center(certificateBodyText, certificateBodyTextBg);
			scene.alignTo.BottomCenter(medal, certificateBodyTextBg, 0, -30);

			this.scene.alignIn.Center(textGroup, backer, 0, 30);
			this.scene.alignIn.Center(bubble, textGroup);
			this.scene.alignIn.Center(storyText, bubble);

			scene.alignIn.TopCenter(headerBg, backer, 0, headerBg.displayHeight / 2);
			scene.alignIn.Center(headerText, headerBg);
			scene.alignIn.BottomLeft(leftDeco, backer, leftDeco.displayWidth / 2, 50);
			scene.alignIn.BottomRight(rightDeco, backer, rightDeco.displayWidth / 2, 50);

			if (this._currentPage == 0) {
				scene.alignIn.BottomCenter(nextButton, backer, 0, 50);
				scene.alignTo.LeftCenter(downloadBtn, nextButton, 15, 0);
			} else {
				scene.alignIn.BottomCenter(downloadBtn, backer, -130, 50);
				scene.alignIn.BottomCenter(nextButton, backer, 130, 50);
			}
		};
		this.resize();
	}

	nextPage(): void {
		this._currentPage++;
		if (this._currentPage == 2) this.close();
		else {
			// turn on certificate assets and turn off story assets
			this.image.setVisible(false);
			this.storyTextGroup.setVisible(false);
			this.nextButton.setTexture("homeCompleteBtn");

			this.headerText.setText(Strings.get("Download"));
			this.certificateBacker.setVisible(true);
			this.certificate.setVisible(true);
			this.downloadButton.setVisible(true);
			this.resize();
		}
	}

	private download(): void {
		//download the certificate
		const width = this.certificate.width;
		const height = this.certificate.height;

		this.scene.game.renderer.snapshotArea(this.certificate.x - width / 2, this.certificate.y - height / 2, width, height, function (
			image
		) {
			if (image instanceof HTMLImageElement) {
				const a = document.createElement("a");
				a.href = image.src;
				a.download = "certificate.png";
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
			}
		});
	}

	private close(): void {
		if (this._onComplete) {
			if (this._onCompleteContext) this._onComplete.call(this._onCompleteContext);
			else this._onComplete();
		}
		this.destroy();
		if (this.certificate) {
			this.certificate.destroy();
			this.certificate = null;
		}
	}

	resize(): void {
		super.resize();
		if (this._onResize) this._onResize();
	}
}
